/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {UntypedFormGroup} from "@angular/forms";
import {TP_ALL_SECTIONS} from "@doctor-panel-modules/patient-treatment-plan/models/tp-sections-info";
import {StoreAction} from "@trec/components/store";
import {Observable} from "rxjs";
import {map, startWith} from "rxjs/operators";
import {RegistryContexts} from "../constants/registry-constants";

/**
 * Abstract class representing a configuration section.
 */
export abstract class ConfigSectionComponent {

  /**
   * Flag indicating whether configuration has been touched.
   */
  abstract get touched$(): Observable<boolean>;

  /**
   * Flag indicating whether configuration is in a valid state.
   */
  abstract get valid$(): Observable<boolean>;
  /**
   * Flag indicating whether config section is performing an operation or not.
   */
  abstract get loading$(): Observable<boolean>;

  /**
   * Current selected context.
   */
  abstract context: RegistryContexts[];

  /**
   * Flag indicating whether configuration is in a valid state.
   */
  get invalid$(): Observable<boolean> {
    return this.valid$.pipe(map((v) => !v));
  }

  /**
   * Info about treatment plan sections.
   */
  TP_SECTIONS_INFO = TP_ALL_SECTIONS;
  /**
   * Prepare action to perform to save current configuration.
   *
   * @throws Error if method is called when configuration is in an invalid state or
   * something goes wrong.
   * @returns Action to perform or null if no saving is needed.
   */
  abstract prepareSaveDataAction(): StoreAction | StoreAction[] | null;

  /**
   * Get `valid$` observable from a form.
   * @param form Form from where extract the `valid$` stream.
   */
  protected extractValidFromForm(form: UntypedFormGroup): Observable<boolean> {
    return form.statusChanges.pipe(
      startWith(form.status),
      map((status) => status === "VALID")
    );
  }

  /**
   * Reset the touched value;
   */
  public abstract resetTouched(): void;

}
